import styled from 'styled-components';
import { useState } from 'react';
import { useRouter } from 'next/router';

import { ChangeCurrencyModal } from 'checkout/components/Payment/ChangeCurrencyModal';
import { FeatureFlags } from 'featureFlags/constants';
import { PaymentVendor } from 'checkout/enums/PaymentVendor';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import {
  shouldShowAffirm,
  shouldShowAfterpay,
  shouldShowAlipay,
  shouldShowApplePay,
  shouldShowGiropay,
  shouldShowKlarna,
  shouldShowPayPal,
} from 'checkout/utils/orderUtils';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { guestPurchasingGiftCard, purchasingGiftCard } from 'checkout/utils/giftCards';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { variableFontRegular } from 'styles/theme';
import { getRecommendedCurrency } from 'checkout/utils/getRecommendedCurrency';
import { useCurrency } from 'shared/hooks/useCurrency';

const TP = 'checkout.components.Payment.PaymentSelector';

interface IPaymentSelectorProps {
  onPaymentSelectClick: (paymentVendor: PaymentVendor) => void;
  selection: PaymentVendor;
}

export const PaymentSelector = ({ onPaymentSelectClick, selection }: IPaymentSelectorProps) => {
  const router = useRouter();

  const { applePayAvailable, order, isGuestCheckout, availablePaymentMethods } =
    useCheckoutContext();
  const { isAuthenticated } = useStoreSelector((state) => state.auth);

  const {
    affirm: affirmAvailability,
    afterpay: afterpayAvailability,
    alipay: alipayAvailability,
    giropay: giropayAvailability,
    klarna: klarnaAvailability,
    paypal: paypalAvailability,
  } = availablePaymentMethods || {};

  const isCheckoutPurchaseGiftCardsEnabled = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_PURCHASE_GIFT_CARDS,
  );
  const isWebEnableCheckoutGiftCardsAllowGuestPurchase = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_CHECKOUT_GIFT_CARDS_ALLOW_GUEST_PURCHASE,
  );

  const isGuestPurchasingGiftCard = guestPurchasingGiftCard({
    giftCardId: router?.query?.giftCardId,
    isAuthenticated,
    isGuestCheckout,
    isWebEnableCheckoutGiftCardsAllowGuestPurchase,
  });
  const isPurchasingGiftCard = purchasingGiftCard({
    giftCardId: order?.product?.giftCardId,
    isGuestPurchasingGiftCard,
    isCheckoutPurchaseGiftCardsEnabled,
  });

  const { selectedCurrencyCode } = useCurrency();

  const [showChangeCurrencyModal, setShowChangeCurrencyModal] = useState(false);
  const [temporarySelectedPaymentVendor, setTemporarySelectedPaymentVendor] = useState(null);

  const showAffirm = shouldShowAffirm({
    affirmAvailability,
    isPurchasingGiftCard,
  });

  const showAfterpay = shouldShowAfterpay({
    afterpayAvailability,
    isPurchasingGiftCard,
  });

  const showAlipay = shouldShowAlipay({
    alipayAvailability,
    isPurchasingGiftCard,
  });

  const showApplePay = shouldShowApplePay({
    applePayAvailable,
    order,
  });

  const showGiropay = shouldShowGiropay({
    giropayAvailability,
    isPurchasingGiftCard,
  });

  const showKlarna = shouldShowKlarna({
    klarnaAvailability,
    isPurchasingGiftCard,
  });

  const showPayPal = shouldShowPayPal({
    paypalAvailability,
    isPurchasingGiftCard,
  });

  const handlePaymentSelectClick = (event, paymentVendor: PaymentVendor) => {
    event.preventDefault();
    setTemporarySelectedPaymentVendor(paymentVendor);

    const recommendedCurrency = getRecommendedCurrency({
      availablePaymentMethods,
      paymentVendor,
      userCurrency: selectedCurrencyCode,
    });

    const shouldChangeCurrency = recommendedCurrency !== selectedCurrencyCode;

    if (shouldChangeCurrency) {
      setShowChangeCurrencyModal(true);
    } else {
      onPaymentSelectClick(paymentVendor);
    }
  };

  return (
    <PaymentSelector.Wrapper>
      <PaymentSelector.Box data-qa="payment_selector_box">
        <PaymentSelector.PaymentOptionWrapper
          data-qa="checkout_payment_selector_option_credit_card"
          $selected={selection === PaymentVendor.CreditCard}
          onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.CreditCard)}
        >
          <PaymentSelector.Anchor aria-label="card" href="">
            {t(`${TP}.card`, 'Card')}
          </PaymentSelector.Anchor>
        </PaymentSelector.PaymentOptionWrapper>
        {showApplePay && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_apple_pay"
            $selected={selection === PaymentVendor.ApplePay}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.ApplePay)}
          >
            <PaymentSelector.Anchor aria-label="apple pay" href="">
              <PaymentSelector.Img src="/images/icons/applepay.png" alt="apple pay" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showKlarna && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_klarna"
            $selected={selection === PaymentVendor.Klarna}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.Klarna)}
          >
            <PaymentSelector.Anchor aria-label="klarna" href="">
              <PaymentSelector.Img src="/images/icons/klarna.svg" alt="klarna" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showAffirm && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_affirm"
            $selected={selection === PaymentVendor.Affirm}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.Affirm)}
          >
            <PaymentSelector.Anchor aria-label="affirm" href="">
              <PaymentSelector.Img src="/images/icons/affirm.png" alt="affirm" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showPayPal && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_paypal"
            $selected={selection === PaymentVendor.PayPal}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.PayPal)}
          >
            <PaymentSelector.Anchor aria-label="paypal" href="">
              <PaymentSelector.Img src="/images/icons/paypal.png" alt="paypal" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showAlipay && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_alipay"
            $selected={selection === PaymentVendor.AliPay}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.AliPay)}
          >
            <PaymentSelector.Anchor aria-label="alipay" href="">
              <PaymentSelector.Img src="/images/icons/alipay.png" alt="Alipay logo" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showAfterpay && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_afterpay"
            $selected={selection === PaymentVendor.AfterPay}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.AfterPay)}
          >
            <PaymentSelector.Anchor aria-label="afterpay" href="">
              <PaymentSelector.AfterPayLogo src="/images/icons/afterpay.svg" alt="AfterPay logo" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
        {showGiropay && (
          <PaymentSelector.PaymentOptionWrapper
            data-qa="checkout_payment_selector_option_giropay"
            $selected={selection === PaymentVendor.Giropay}
            onClick={(event) => handlePaymentSelectClick(event, PaymentVendor.Giropay)}
          >
            <PaymentSelector.Anchor aria-label="giropay" href="">
              <PaymentSelector.Img src="/images/icons/giropay.png" alt="Giropay logo" />
            </PaymentSelector.Anchor>
          </PaymentSelector.PaymentOptionWrapper>
        )}
      </PaymentSelector.Box>
      {showChangeCurrencyModal && (
        <ChangeCurrencyModal
          onClose={() => setShowChangeCurrencyModal(false)}
          selectedPaymentVendor={temporarySelectedPaymentVendor}
          selectPaymentVendor={onPaymentSelectClick}
        />
      )}
    </PaymentSelector.Wrapper>
  );
};

PaymentSelector.Wrapper = styled.div`
  display: flex;
  margin-bottom: 48px;
  ${variableFontRegular({ size: '11px', height: '16px' })};

  -webkit-overflow-scrolling: touch;
  ${hideScrollbars};

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    margin-bottom: 24px;
  }
`;

PaymentSelector.Box = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.xSmall}px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

PaymentSelector.PaymentOptionWrapper = styled.div<{ $selected: boolean }>`
  height: 32px;
  width: 80px;
  display: flex;

  justify-content: center;
  align-items: center;
  flex: 0 0;
  border: ${({ $selected, theme }) => ($selected ? '2px solid black' : theme.borders.black)};
`;

PaymentSelector.Anchor = styled.a`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

PaymentSelector.Img = styled.img`
  width: 41px;
`;
PaymentSelector.AfterPayLogo = styled.img`
  width: 50px;
`;
